import React from 'react';
import { X, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BulkDeleteModalProps {
  show: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  deletingRows?: Set<string>;
  selectedRows: string[];
}

const BulkDeleteModal: React.FC<BulkDeleteModalProps> = ({ show, onCancel, onConfirm, deletingRows = new Set(), selectedRows }) => {
  const { t } = useTranslation();
  if (!show) return null;
  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter') {
      if (!deletingRows.size) onConfirm();
    }
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {selectedRows.length === 1 ? t('deleteConfirmation') : t('deleteSelectedRecords', { count: selectedRows.length })}
        </h3>
        <p className="text-gray-600 mb-6">
          {selectedRows.length === 1 ? t('deleteConfirmationDesc') : t('deleteSelectedRecordsDesc', { count: selectedRows.length })}
        </p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300 transition-colors flex items-center justify-center"
            disabled={deletingRows.size > 0}
            aria-label="Cancel"
          >
            <X className="h-5 w-5" />
          </button>
          <button
            onClick={onConfirm}
            onKeyDown={handleKeyDown}
            disabled={deletingRows.size > 0}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            aria-label="Delete"
          >
            {deletingRows.size > 0 ? (
              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <Trash2 className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkDeleteModal;
